<template>
  <div v-if="mountedLoadingEnd">
    <div class="columns is-align-items-center">
      <div class="column is-5">
        <p> {{ $t(`${baseTrad}.fairePar`) }} </p>
      </div>
      <div class="column">
        <tiers-autocomplete
          v-if="!detail"
          v-model="prestationFournisseur"
          @input="setPrestationFournisseur"
          :societeActiveId="societeActiveId"
          :type="constantes.TIERS_TYPE_FOURNISSEUR"
          :sousTypes="constantes.SOUS_TYPES"
          :filteredItemFunc="(tiers) => tiers.id !== data.prestationFournisseurId"
          :initialValue="data.prestationFournisseurId"
          :disabled="detail"
          :title-pop-up-advanced-search="`${baseTrad}.advancedSearchFournisseur`"
          :msg-error="msgError.prestationFournisseur"
          search-by-competence-fournisseur
          :injectItems="[annonceurCommande]"
          advanced-search
          expanded>
          <template slot-scope="{ option }">
            <p>
              <span v-if="option.code">{{ option.code }} </span>
              <span>{{ option.nomCommercial }}</span>
            </p>
            <p class="is-size-8 is-italic">
              <span v-if="option.compteComptable">{{ option.compteComptable }} </span>
              <span>{{ option.adressePostaleLibelle }}</span>
            </p>
          </template>
        </tiers-autocomplete>
        <router-link-blank
          v-else-if="prestationFournisseur && prestationFournisseur.id"
          :message="prestationFournisseur.nomCommercial"
          :to="{ name: 'c.achat.fournisseurs', params: { id: prestationFournisseur.id } }"
          :class="{'text-access-no-permission': !checkPermissions(constantes.FOURNISSEURS.lecture)}"
          class="lien">
          <b-tooltip v-if="msgError.prestationFournisseur"
            :label="msgError.prestationFournisseur"
            type="is-warning"
            position="is-left">
            <b-icon class="mgl-2" icon="alert" type="is-warning" size="is-small"></b-icon>
          </b-tooltip>
        </router-link-blank>
        <span v-else-if="data.annonceurCommande">{{ this.ANNONCEUR_COMMANDE }}</span>
      </div>
    </div>
    <div class="columns is-align-items-center">
      <div class="column is-5">
        <p> {{ $t(`${baseTrad}.prestation`) }} </p>
      </div>
      <div class="column">
        <produit-autocomplete
          v-if="!detail"
          v-model="produitPrestation"
          @input="setDataId('produitPrestationId', $event)"
          :societeActiveId="societeActiveId"
          :sens="constantes.SENS_ACHAT"
          :categories="[constantes.CATEGORIE_IMPRESSION_AFFICHE]"
          :filteredItemFunc="(produit) => produit.id !== data.produitPrestationId"
          :initialValue="data.produitPrestationId"
          :disabled="detail"
          :msg-error="msgError.produitPrestation"
          expanded
        />
        <router-link-blank
          v-else-if="produitPrestation"
          :message="produitPrestation.libelle"
          :to="{name: 'c.bodac.catalogue-impressionAffiche', params: { id: produitPrestation.id }}"
          :class="{'text-access-no-permission': !checkPermissions(constantes.CATALOGUE.lecture)}"
          class="lien">
          <b-tooltip v-if="msgError.produitPrestation"
            :label="msgError.produitPrestation"
            type="is-warning"
            position="is-left">
            <b-icon class="mgl-2" icon="alert" type="is-warning" size="is-small"></b-icon>
          </b-tooltip>
        </router-link-blank>
      </div>
    </div>
    <div class="columns is-align-items-center">
      <div class="column is-5">
        <p> {{ $t(`${baseTrad}.livraison`) }} </p>
      </div>
      <div class="column">
        <tiers-autocomplete
          v-if="!detail"
          v-model="lieuLivraison"
          @input="setDataId('lieuLivraisonId', $event)"
          :societeActiveId="societeActiveId"
          :type="constantes.TIERS_TYPE_FOURNISSEUR"
          :sousTypes="[constantes.TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON]"
          :filteredItemFunc="(tiers) => tiers.id !== data.lieuLivraisonId"
          :initialValue="data.lieuLivraisonId"
          :disabled="detail"
          :title-pop-up-advanced-search="`${baseTrad}.advancedSearchLieuDeLivraison`"
          :msg-error="msgError.lieuLivraison"
          search-by-competence-fournisseur
          advanced-search
          expanded>
          <template slot-scope="{ option }">
            <p>
              <span v-if="option.code">{{ option.code }} </span>
              <span>{{ option.nomCommercial }}</span>
            </p>
            <p class="is-size-8 is-italic">
              <span v-if="option.compteComptable">{{ option.compteComptable }} </span>
              <span>{{ option.adressePostaleLibelle }}</span>
            </p>
          </template>
        </tiers-autocomplete>
        <router-link-blank
          v-else-if="lieuLivraison"
          :message="lieuLivraison.nomCommercial"
          :to="{ name: 'c.achat.fournisseurs', params: { id: lieuLivraison.id } }"
          :class="{'text-access-no-permission': !checkPermissions(constantes.FOURNISSEURS.lecture)}"
          class="lien">
          <b-tooltip v-if="msgError.lieuLivraison"
            :label="msgError.lieuLivraison"
            type="is-warning"
            position="is-left">
            <b-icon class="mgl-2" icon="alert" type="is-warning" size="is-small"></b-icon>
          </b-tooltip>
        </router-link-blank>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import TiersAutocomplete from '@/components/general/autocomplete/tiersAutocomplete';
import ProduitAutocomplete from '@/components/general/autocomplete/produitAutocomplete';
import {
  TIERS_TYPE_FOURNISSEUR,
  TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON,
  LISTE_SOUS_TYPE_FOURNISSEURS_PAGES,
} from '@/configuration/tiers/tiersConf';
import { SENS_ACHAT } from '@/configuration/devisConf';
import { CATEGORIE_IMPRESSION_AFFICHE } from '@/configuration/catalogue/produitConf';
import { FOURNISSEURS } from '@/configuration/achat/permissionsConf';
import { CATALOGUE } from '@/configuration/permissionsBodacConf';

const Societe = createNamespacedHelpers('societe');
const Permissions = createNamespacedHelpers('permissions');

export default {
  name: 'EtapeInformation',
  props: {
    data: {
      type: Object,
      required: true,
    },
    originData: {
      type: Object,
      required: false,
    },
    detail: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TiersAutocomplete,
    ProduitAutocomplete,
    RouterLinkBlank,
  },
  data() {
    return {
      mountedLoadingEnd: false,
      lieuLivraison: null,
      produitPrestation: null,
      prestationFournisseur: null,
      ANNONCEUR_COMMANDE: this.$t('editionFormatAffiche.etapeInformation.annonceurCommande'),
      constantes: {
        CATALOGUE,
        SENS_ACHAT,
        FOURNISSEURS,
        TIERS_TYPE_FOURNISSEUR,
        CATEGORIE_IMPRESSION_AFFICHE,
        TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON,
        SOUS_TYPES: LISTE_SOUS_TYPE_FOURNISSEURS_PAGES
          .map(el => el.code)
          .filter(el => el !== TIERS_SOUS_TYPE_LIEU_DE_LIVRAISON),
      },
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    annonceurCommande() {
      return { code: '', nomCommercial: this.ANNONCEUR_COMMANDE, adressePostaleLibelle: '' };
    },
    baseTrad() {
      if (this.detail) return 'editionFormatAffiche.etapeInformationDetail';
      return 'editionFormatAffiche.etapeInformation';
    },
    msgError() {
      const msg = {};
      if (!!this.originData
        && this.originData.prestationFournisseurId !== this.data.prestationFournisseurId) {
        msg.prestationFournisseur = this.$t('editionFormatAffiche.etapeInformation.msgErrorSelection');
      }
      if (!!this.originData
        && this.originData.produitPrestationId !== this.data.produitPrestationId) {
        msg.produitPrestation = this.$t('editionFormatAffiche.etapeInformation.msgErrorSelection');
      }
      if (!!this.originData
        && this.originData.lieuLivraisonId !== this.data.lieuLivraisonId) {
        msg.lieuLivraison = this.$t('editionFormatAffiche.etapeInformation.msgErrorSelection');
      }
      return msg;
    },
  },
  methods: {
    setPrestationFournisseur(item) {
      this.data.annonceurCommande = item?.nomCommercial === this.ANNONCEUR_COMMANDE;
      this.setDataId('prestationFournisseurId', item);
    },
    setDataId(property, val) {
      this.data[property] = val?.id || null;
    },
  },
  mounted() {
    this.lieuLivraison = this.data.lieuLivraison;
    this.produitPrestation = this.data.produitPrestation;
    if (this.data.annonceurCommande) {
      this.prestationFournisseur = this.annonceurCommande;
    } else {
      this.prestationFournisseur = this.data.prestationFournisseur;
    }
    this.mountedLoadingEnd = true;
  },
};
</script>
